import { clearLocalStorage, getUserManager, rolesManipulator } from 'auth';
import { decodeJwt } from 'jose';
import {
  type TokenPayload,
  areRoleSetsEqual,
  getComplianceRoles,
} from '@/modules/common/api/session-recovery/roles.ts';
import { BUSINESS_QUERY_PARAM } from '@/modules/common/constants/search-params.ts';
import { queryClient } from '@/modules/common/query.ts';

export type SessionRecoveryDependencies = {
  refresh: () => Promise<{ access_token?: string } | null | undefined>;
  removeUser: () => Promise<void>;
  clearAppState: () => void;
  clearAuthState: () => void;
  clearQueryCache: () => Promise<unknown> | unknown;
  redirectToAppSelector: () => void;
  getAccessToken: () => Promise<string | undefined>;
  getCurrentRole: () => string | null;
  getCurrentBusinessId: () => string | null;
  decodeToken: (token?: string) => TokenPayload | null;
};

export type SessionRecovery = {
  recover: () => Promise<boolean>;
  expire: () => Promise<void>;
  isRedirecting: () => boolean;
};

export const createSessionRecovery = (
  dependencies: SessionRecoveryDependencies,
): SessionRecovery => {
  let refreshPromise: Promise<boolean> | null = null;
  let isRedirecting = false;

  const redirect = async (removeUser: boolean) => {
    if (isRedirecting) return;
    isRedirecting = true;

    dependencies.clearAppState();
    if (removeUser) dependencies.clearAuthState();
    await dependencies.clearQueryCache();
    if (removeUser) {
      try {
        await dependencies.removeUser();
      } catch {
        // Storage has already been cleared; continue to the selector.
      }
    }
    dependencies.redirectToAppSelector();
  };

  const recover = () => {
    if (isRedirecting) return Promise.resolve(false);
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
      try {
        const businessId = dependencies.getCurrentBusinessId();
        const previousRoles = getComplianceRoles(
          dependencies.decodeToken(await dependencies.getAccessToken()),
          businessId,
        );
        const user = await dependencies.refresh();
        if (!user?.access_token) {
          await redirect(true);
          return false;
        }

        const refreshedRoles = getComplianceRoles(
          dependencies.decodeToken(user.access_token),
          businessId,
        );
        const currentRole = dependencies.getCurrentRole();
        const rolesChanged = !areRoleSetsEqual(previousRoles, refreshedRoles);
        const currentRoleRemoved = currentRole !== null && !refreshedRoles.includes(currentRole);
        const noComplianceAccess = refreshedRoles.length === 0;

        if (rolesChanged || currentRoleRemoved || noComplianceAccess) {
          await redirect(false);
          return false;
        }
        return true;
      } catch {
        await redirect(true);
        return false;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  };

  return { recover, expire: () => redirect(true), isRedirecting: () => isRedirecting };
};

const decodeToken = (token?: string) => {
  if (!token) return null;
  try {
    return decodeJwt(token) as TokenPayload;
  } catch {
    return null;
  }
};

export const createComplianceSessionRecovery = () =>
  createSessionRecovery({
    refresh: async () => getUserManager()?.signinSilent(),
    removeUser: async () => getUserManager()?.removeUser(),
    clearAppState: () => {
      localStorage.removeItem(BUSINESS_QUERY_PARAM);
      localStorage.removeItem('vestra_current_user_role');
      sessionStorage.clear();
    },
    clearAuthState: () => clearLocalStorage(),
    clearQueryCache: async () => {
      await queryClient.cancelQueries();
      queryClient.removeQueries();
    },
    redirectToAppSelector: () => window.location.replace(import.meta.env.VITE_APP_SELECTOR_URL),
    getAccessToken: async () => (await getUserManager()?.getUser())?.access_token,
    getCurrentRole: () => rolesManipulator.current_user_role,
    getCurrentBusinessId: () => new URLSearchParams(location.search).get(BUSINESS_QUERY_PARAM),
    decodeToken,
  });
